import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Badge, Button, Skeleton } from '@roger/ui-components';
import { X } from 'lucide-react';
import type { TaskListResponse } from '../services/api/types.gen';
import { parseISOToLocalDate } from '../utils/dateUtils';
import {
  getPdgmPeriodBoundaries,
  isPdgmPeriodBoundaryStart,
  type PdgmPeriodBoundaries,
} from '../utils/pdgmPeriodUtils';
import { getClinicianDisplayName } from '../utils/clinicianUtils';
import { useMediaQuery } from '../hooks/useIsMobile';
import { useCalendarVisitPopout } from '../hooks/useCalendarVisitPopout';
import type { CalendarCreateVisitClinician } from './CalendarCreateVisitDialog';
import LupaThresholdGauge from './LupaThresholdGauge';
import AuthorizationBudgetStrip from './AuthorizationBudgetStrip';
import type { EpisodeLupaPeriodResponse } from '../services/api';
import type { AuthorizationBudget } from '../services/api/types.gen';

interface VisitsCalendarPanelProps {
  visits: TaskListResponse[];
  onVisitClick: (visit: TaskListResponse) => void;
  onCreateTaskFromDate: (date: Date) => void;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  clinicians: CalendarCreateVisitClinician[];
  /** Set of display names that appear for more than one distinct user. */
  duplicateClinicianNames?: Set<string>;
  /** Per-30-day-period LUPA thresholds (ROG-2493); omit when not applicable. */
  lupaPeriods?: EpisodeLupaPeriodResponse[];
  /** True while `lupaPeriods` is being fetched, so the strip holds its space (EMR-1161). */
  lupaLoading?: boolean;
  /** Server-computed per-authorization used/allowed rows (EMR-706); omit when not applicable. */
  authorizationBudget?: AuthorizationBudget[];
  /** True while `authorizationBudget` is being fetched (EMR-1161). */
  authorizationBudgetLoading?: boolean;
  /** True when the episode's insurance requires an authorization on file (EMR-706). */
  requiresAuthorization?: boolean;
  /** How many visits spend an authorized visit but aren't linked to one (EMR-706). */
  unlinkedVisitCount?: number;
  /**
   * The authorization request failed, so used/allowed is unknown. Rendered as
   * an explicit "unavailable" note -- otherwise a failed fetch is
   * indistinguishable from "this episode needs no authorization".
   */
  authorizationBudgetUnavailable?: boolean;
}

const PDGM_PERIOD_BOUNDARY_DAY_CLASS =
  "relative before:pointer-events-none before:absolute before:left-0 before:top-1/2 before:z-30 before:h-[85%] before:w-[3px] before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-gray-900 before:content-['']";

/**
 * Placeholder rows for a strip still being fetched, in the same shape the real
 * one lands in: a short label line and a full-width bar per row (EMR-1161).
 * Without it the column has no width until the data arrives and the calendar
 * jumps sideways when it does.
 */
const StripSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <div className='space-y-3' data-testid='strip-skeleton'>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index}>
        <div className='mb-1 flex items-center justify-between gap-2'>
          <Skeleton className='h-3 w-20' />
          <Skeleton className='h-3 w-12' />
        </div>
        <Skeleton className='h-1.5 w-full rounded-full' />
      </div>
    ))}
  </div>
);

const getStatusVariant = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'default';
    case 'scheduled':
      return 'secondary';
    case 'in_progress':
      return 'outline';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
};

const VisitsCalendarPanel: React.FC<VisitsCalendarPanelProps> = ({
  visits,
  onVisitClick,
  onCreateTaskFromDate,
  clinicians,
  episodeStartDate,
  episodeEndDate,
  lupaPeriods,
  lupaLoading,
  authorizationBudget,
  authorizationBudgetLoading,
  requiresAuthorization,
  unlinkedVisitCount,
  authorizationBudgetUnavailable,
}) => {
  // Three months need ~730px of calendar, which the content area no longer has
  // much below `lg` once the nav sidebar and page/card padding are taken out —
  // and one month rendered whole beats three with the last hidden behind a
  // horizontal scrollbar. No CSS equivalent: `numberOfMonths` is a prop.
  const showThreeMonths = useMediaQuery('(min-width: 1024px)');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [popoutVisible, setPopoutVisible] = useState(false);
  const [popoutPosition, setPopoutPosition] = useState({ x: 0, y: 0 });
  const [popoutDate, setPopoutDate] = useState<Date | undefined>();
  const calendarRef = useRef<HTMLDivElement>(null);
  const popoutRef = useRef<HTMLDivElement>(null);

  const episodeDateRange = useMemo(() => {
    if (episodeStartDate && episodeEndDate) {
      const start = parseISOToLocalDate(episodeStartDate);
      const end = parseISOToLocalDate(episodeEndDate);
      if (start && end) return { start, end };
    }
    return null;
  }, [episodeStartDate, episodeEndDate]);

  const pdgmBoundaries = useMemo((): PdgmPeriodBoundaries | null => {
    if (lupaPeriods && lupaPeriods.length >= 2) {
      const period1End = parseISOToLocalDate(lupaPeriods[0].end_date);
      const period2Start = parseISOToLocalDate(lupaPeriods[1].start_date);
      if (period1End && period2Start) {
        return {
          periodCount: 2,
          period1End,
          period2Start,
        };
      }
    }
    if (episodeDateRange) {
      return getPdgmPeriodBoundaries(
        episodeDateRange.start,
        episodeDateRange.end
      );
    }
    return null;
  }, [lupaPeriods, episodeDateRange]);

  const hasTwoPdgmPeriods = pdgmBoundaries?.periodCount === 2;

  // A block in flight holds its own space: the two strips give the column its
  // width, so appearing late shifts the calendar sideways (EMR-1161).
  const showLupaThresholds = (lupaPeriods?.length ?? 0) > 0 || !!lupaLoading;

  // Mirrors the LUPA strip's own gate (only render when there's real content)
  // rather than always showing an empty "Authorization budget" section.
  // Deliberately not gated on `authorizationBudgetLoading`. Doing so rendered
  // the heading and skeleton for every episode on first load — self-pay ones
  // included — and then tore the whole block back out when the fetch resolved
  // `requires_authorization: false`, reflowing the LUPA strip beside it
  // (`justify-around`). Waiting until the episode is known to need
  // authorization never promises a budget that isn't coming; PatientDetail's
  // own on-screen ref keeps the skeleton for re-fetches, where the block has
  // already earned its place.
  const showAuthorizationBudget =
    (authorizationBudget?.length ?? 0) > 0 ||
    (requiresAuthorization ?? false) ||
    (authorizationBudgetUnavailable ?? false);

  const isPdgmPeriodBoundaryDay = useMemo(() => {
    if (!hasTwoPdgmPeriods || !pdgmBoundaries) {
      return undefined;
    }
    const boundaries = pdgmBoundaries;
    return (date: Date) => isPdgmPeriodBoundaryStart(date, boundaries);
  }, [hasTwoPdgmPeriods, pdgmBoundaries]);

  const defaultMonth = useMemo(() => {
    if (episodeDateRange) {
      return new Date(
        episodeDateRange.start.getFullYear(),
        episodeDateRange.start.getMonth(),
        1
      );
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, [episodeDateRange]);

  const dateRange = useMemo(() => {
    const from = defaultMonth;
    const toDate = new Date(from.getFullYear(), from.getMonth() + 3, 0);
    return { fromDate: from, toDate };
  }, [defaultMonth]);

  const visitsByDate = useMemo(() => {
    const grouped = new Map<string, TaskListResponse[]>();
    for (const visit of visits) {
      const visitDate = parseISOToLocalDate(
        visit.task_date ?? visit.scheduled_date
      );
      if (
        !visitDate ||
        visitDate < dateRange.fromDate ||
        visitDate > dateRange.toDate
      )
        continue;
      const dateKey = visitDate.toDateString();
      if (!grouped.has(dateKey)) grouped.set(dateKey, []);
      grouped.get(dateKey)!.push(visit);
    }
    for (const dayVisits of grouped.values()) {
      dayVisits.sort((left, right) => {
        const leftDate = left.task_date ?? left.scheduled_date;
        const rightDate = right.task_date ?? right.scheduled_date;
        return (
          leftDate.localeCompare(rightDate) || left.id.localeCompare(right.id)
        );
      });
    }
    return grouped;
  }, [visits, dateRange]);

  // Separate completed days (all visits done) from pending days (any unfinished)
  const visitDateSets = useMemo(() => {
    const completed = new Set<string>();
    const pending = new Set<string>();
    visitsByDate.forEach((dayVisits, dateKey) => {
      if (
        dayVisits.every(
          v => v.status === 'completed' || v.status === 'exported'
        )
      )
        completed.add(dateKey);
      else pending.add(dateKey);
    });
    return { completed, pending };
  }, [visitsByDate]);

  const handleDateClick = (date: Date, event?: React.MouseEvent) => {
    if (
      episodeDateRange &&
      (date < episodeDateRange.start || date > episodeDateRange.end)
    ) {
      return;
    }

    setSelectedDate(date);
    const dateKey = date.toDateString();
    const dayVisits = visitsByDate.get(dateKey);

    if (!dayVisits || dayVisits.length === 0) {
      setPopoutVisible(false);
      onCreateTaskFromDate(date);
    } else {
      if (event) {
        setPopoutPosition({
          x: event.clientX,
          y: event.clientY + 10,
        });
      }
      setPopoutDate(date);
      setPopoutVisible(true);
    }
  };

  useCalendarVisitPopout({
    popoutVisible,
    popoutRef,
    popoutPosition,
    setPopoutPosition,
    popoutDate,
    visitsByDate,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        calendarRef.current?.contains(target) ||
        popoutRef.current?.contains(target)
      ) {
        return;
      }

      setPopoutVisible(false);
    };
    if (popoutVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [popoutVisible]);

  const renderVisitPopout = () => {
    if (!popoutVisible || !popoutDate) return null;
    const dateKey = popoutDate.toDateString();
    const dayVisits = visitsByDate.get(dateKey);
    if (!dayVisits || dayVisits.length === 0) return null;

    const popout = (
      <div
        ref={popoutRef}
        data-testid='calendar-visit-popout'
        className='fixed z-[1000] bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[min(16rem,calc(100vw-2rem))] max-w-[min(20rem,calc(100vw-2rem))]'
        style={{
          left: `${popoutPosition.x}px`,
          top: `${popoutPosition.y}px`,
          transform: 'translateX(-50%)',
        }}
      >
        <div className='flex items-center justify-between mb-2'>
          <div className='font-medium text-xs text-gray-700'>
            {popoutDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            })}
          </div>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setPopoutVisible(false)}
            className='h-5 w-5 p-0'
          >
            <X className='h-3 w-3' />
          </Button>
        </div>
        <div className='space-y-1.5 max-h-48 overflow-y-auto'>
          {dayVisits.map(visit => (
            <div
              key={visit.id}
              className='flex items-center justify-between p-1.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors'
              onClick={() => {
                onVisitClick(visit);
                setPopoutVisible(false);
              }}
            >
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-1.5 mb-0.5'>
                  <Badge
                    variant={getStatusVariant(visit.status)}
                    className='text-[10px] px-1 py-0'
                  >
                    {visit.display_name}
                  </Badge>
                  <Badge variant='outline' className='text-[10px] px-1 py-0'>
                    {visit.status}
                  </Badge>
                  {visit.missed_visit_data && (
                    <Badge
                      variant='destructive'
                      className='text-[10px] px-1 py-0'
                    >
                      Missed
                    </Badge>
                  )}
                </div>
                <div className='text-[10px] text-gray-700'>
                  {visit.assigned_clinician_id
                    ? (() => {
                        const clinician = clinicians.find(
                          c => c.id === visit.assigned_clinician_id
                        );
                        return clinician
                          ? getClinicianDisplayName(clinician)
                          : 'Clinician not found';
                      })()
                    : 'Not assigned'}
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button
          size='sm'
          className='mt-3 w-full'
          onClick={() => {
            onCreateTaskFromDate(popoutDate);
            setPopoutVisible(false);
          }}
        >
          Add Task
        </Button>
      </div>
    );

    return createPortal(popout, document.body);
  };

  return (
    // Calendar left, LUPA + authorization bars in a column to its right
    // (EMR-1161). The side-by-side row is gated at `2xl`, not `md`: three
    // months need ~730px and the column takes another 224px, so anything
    // narrower squeezes the calendar into a horizontally-scrolling sliver.
    // Below `xl` the column falls back under the calendar, which keeps the
    // full row width it had before this change.
    //
    // `xl` (1280px) is the viewport, not the space this row gets: the nav
    // sidebar and the page/card padding take ~300px, leaving ~980px. A 320px
    // column needed ~1050px and hid the third month behind the wrapper's
    // `overflow-x-auto`, so the row was gated at `2xl` — which no laptop
    // reaches (a 14" MBP is 1512px). At `w-56` the pair needs ~954px and
    // fits, so the strips sit beside the calendar on the screens schedulers
    // actually use.
    <div className='flex flex-col gap-2 lg:gap-4 xl:flex-row xl:items-stretch xl:justify-around xl:gap-0'>
      <div className='min-w-0'>
        <div
          className='relative flex justify-center overflow-x-auto'
          ref={calendarRef}
        >
          {/* Calendar rendered at natural w-fit size, centered, not stretched */}
          <Calendar
            mode='single'
            selected={selectedDate}
            defaultMonth={defaultMonth}
            onSelect={date => {
              if (date) handleDateClick(date);
            }}
            onDayClick={(date, _modifiers, event) => {
              handleDateClick(date, event);
            }}
            disabled={[
              { before: dateRange.fromDate },
              { after: dateRange.toDate },
            ]}
            numberOfMonths={showThreeMonths ? 3 : 1}
            showOutsideDays={false}
            className='max-lg:p-1 max-lg:[--cell-size:1.75rem]'
            classNames={{
              // Remove built-in bg-accent on today so our modifiersStyles control the background
              today: 'rounded-md',
            }}
            modifiers={{
              notEpisodeDay: episodeDateRange
                ? (date: Date) =>
                    date < episodeDateRange.start || date > episodeDateRange.end
                : () => false,
              episodeDay: episodeDateRange
                ? (date: Date) =>
                    date >= episodeDateRange.start &&
                    date <= episodeDateRange.end
                : () => true,
              hasPendingVisits: (date: Date) =>
                visitDateSets.pending.has(date.toDateString()),
              hasCompletedVisits: (date: Date) =>
                visitDateSets.completed.has(date.toDateString()),
              ...(isPdgmPeriodBoundaryDay && {
                pdgmPeriodBoundary: isPdgmPeriodBoundaryDay,
              }),
            }}
            modifiersClassNames={{
              ...(isPdgmPeriodBoundaryDay && {
                pdgmPeriodBoundary: PDGM_PERIOD_BOUNDARY_DAY_CLASS,
              }),
            }}
            modifiersStyles={{
              notEpisodeDay: {
                backgroundColor: 'rgb(243 244 246)',
                color: 'rgb(156 163 175)',
              },
              today: {
                boxShadow: 'inset 0 0 0 2px rgb(79 70 229)',
                borderRadius: '6px',
                fontWeight: '600',
              },
              hasPendingVisits: {
                backgroundColor: 'rgb(219 234 254)',
                borderRadius: '4px',
                outline: '2px dashed rgb(147 197 253)',
                outlineOffset: '-2px',
              },
              hasCompletedVisits: {
                backgroundColor: 'rgb(191 219 254)',
                borderRadius: '4px',
                outline: '2px solid rgb(96 165 250)',
                outlineOffset: '-2px',
              },
            }}
          />
          {renderVisitPopout()}
        </div>

        {/* Compact legend — hidden on the phone-width episode view so the
            calendar does not push the task list off-screen. Desktop keeps
            the key because the three-month strip has room for it. */}
        <div className='mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-700 max-lg:hidden'>
          <div className='flex items-center gap-1.5'>
            <div className='w-3 h-3 rounded-sm border border-gray-300 bg-white' />
            <span>Episode Day</span>
          </div>
          <div className='flex items-center gap-1.5'>
            <div
              className='w-3 h-3 rounded-sm'
              style={{
                backgroundColor: 'rgb(219 234 254)',
                outline: '2px dashed rgb(147 197 253)',
                outlineOffset: '-2px',
              }}
            />
            <span>Scheduled Visit</span>
          </div>
          <div className='flex items-center gap-1.5'>
            <div
              className='w-3 h-3 rounded-sm'
              style={{
                backgroundColor: 'rgb(191 219 254)',
                outline: '2px solid rgb(96 165 250)',
                outlineOffset: '-2px',
              }}
            />
            <span>Completed Visit</span>
          </div>
          <div className='flex items-center gap-1.5'>
            <div className='w-3 h-3 rounded-sm bg-gray-100 border border-gray-200' />
            <span>Outside Episode</span>
          </div>
          <div className='flex items-center gap-1.5'>
            <div
              className='w-3 h-3 rounded-sm'
              style={{ boxShadow: 'inset 0 0 0 2px rgb(79 70 229)' }}
            />
            <span>Today</span>
          </div>
          {hasTwoPdgmPeriods && (
            <div className='flex items-center gap-1.5'>
              <div className='relative flex h-4 w-3 items-center justify-center rounded-sm bg-white'>
                <div className='absolute h-full w-[3px] rounded-full bg-gray-900' />
              </div>
              <span>30-day period boundary</span>
            </div>
          )}
        </div>

        {/* The only on-screen cue that days are clickable, so it survives the
            move to a narrower column — on its own line under the legend rather
            than squeezed onto the end of it. `VisitCalendar` carries the same
            line; the two calendars should not disagree about this. */}
        <p className='mt-2 text-center text-xs text-gray-600 max-lg:hidden'>
          Click a date to view visits or create a new appointment
        </p>
      </div>

      {(showLupaThresholds || showAuthorizationBudget) && (
        // The two blocks are spread down the calendar's height rather than
        // stacked at the top, so each gets room to breathe instead of reading
        // as one dense list (EMR-1161).
        //
        // `gap-6` is a floor, not a layout: `justify-around` still spreads the
        // blocks when the calendar leaves slack. Desktop used to drop the gap
        // to zero and lean on that spreading alone, which left the two
        // headings touching as soon as the content filled the column — an
        // episode with two authorizations, say. When the blocks no longer fit
        // in the calendar's height the row grows to hold them (the parent is
        // `xl:items-stretch`), rather than the strip compressing.
        <div className='flex w-full shrink-0 flex-col justify-around gap-6 pt-2 xl:w-56'>
          {/* Rendered whenever the episode has periods, including when no
              threshold is determinable yet: the gauge names what is still
              missing, which an empty space cannot do (EMR-820). */}
          {showLupaThresholds && (
            <div>
              {/* Set in the foreground colour, not grey: these carry the same
                  weight in the row as the calendar's month captions. Both
                  block headings stand 8px off their content — the one rhythm
                  the whole strip is built on (EMR-1161). */}
              <div className='mb-2 select-none text-xs font-semibold uppercase tracking-wider text-foreground'>
                LUPA thresholds
              </div>
              {lupaLoading ? (
                <StripSkeleton rows={2} />
              ) : (
                <LupaThresholdGauge periods={lupaPeriods ?? []} />
              )}
            </div>
          )}

          {showAuthorizationBudget && (
            <div>
              <div className='mb-2 select-none text-xs font-semibold uppercase tracking-wider text-foreground'>
                Auth budget
              </div>
              {authorizationBudgetLoading ? (
                <StripSkeleton rows={2} />
              ) : (
                <AuthorizationBudgetStrip
                  budget={authorizationBudget ?? []}
                  requiresAuthorization={requiresAuthorization ?? false}
                  unlinkedVisitCount={unlinkedVisitCount ?? 0}
                  unavailable={authorizationBudgetUnavailable ?? false}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitsCalendarPanel;
